import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { toast } from "react-toastify";

import { fetchDueInvoicePreviewUrl } from "../../../api/grace-period";
import PdfPreviewModal from "../../Common/UIComponents/PdfPreviewModal";
import CheckoutProcessingModal from "../../MyAccount/UsageMetrics/components/CheckoutProcessingModal";
import SuccessModal from "../../MyAccount/UsageMetrics/components/SuccessModal";
import {
  GYNGER_CHECKOUT_PENDING_MESSAGE,
  GYNGER_SUCCESS_MODAL_AUTO_CLOSE_MS,
} from "../../MyAccount/UsageMetrics/hooks/useGyngerCheckoutPayment";

import { canMakePayment } from "../../../utils/Permissions";
import {
  persistGracePeriodSnapshot,
  readGracePeriodFromSession,
} from "../utils";

import {
  fetchGyngerCheckoutStatus,
  openPaymentSession,
} from "../../../actions/usage";

const getPdfFileName = (pdfPath) => pdfPath?.split("/").pop();

const CHECKOUT_POLL_INTERVAL_MS = 3000;

const PAYMENT_OUTCOME_COPY = {
  success: {
    title: "Payment Successful!",
    description: "Your renewal payment is complete. Result access has been restored.",
  },
  failed: {
    title: "Payment Failed",
    description: "Your payment could not be completed. Please try again.",
  },
  already_paid: {
    title: "Payment already completed",
    description: "There is no outstanding renewal invoice. We are restoring full account access.",
  },
};

const GraceInvoicePaymentContext = createContext({
  isPaymentBusy: false,
  startPayNow: async () => false,
  startViewBillingDetails: async () => false,
});

function getCheckoutStatusToken(value) {
  return String(value || "").toLowerCase();
}

function isCheckoutCompleted(payload = {}) {
  const directStatus = getCheckoutStatusToken(payload.status);
  const resultStatus = getCheckoutStatusToken(payload.result?.status);
  return directStatus === "completed" || resultStatus === "completed";
}

function isCheckoutFailed(payload = {}) {
  const directStatus = getCheckoutStatusToken(payload.status);
  const resultStatus = getCheckoutStatusToken(payload.result?.status);
  return directStatus === "failed" || resultStatus === "failed";
}

function clearGraceLocksInSession() {
  const stored = readGracePeriodFromSession();
  persistGracePeriodSnapshot({
    ...stored,
    grace_period_status: "INACTIVE_GRACE_PERIOD",
    grace_ends_on: null,
    grace_days_left: null,
    grace_amount_due: null,
    grace_days_overdue: null,
    invoice_id: null,
    invoice_amount_due: null,
    invoice_pdf_path: null,
  });
}

export function GraceInvoicePaymentProvider({ children }) {
  const [submitting, setSubmitting] = useState(false);
  const [paymentPending, setPaymentPending] = useState(false);
  const [paymentUrl, setPaymentUrl] = useState("");
  const [processingMessage, setProcessingMessage] = useState(GYNGER_CHECKOUT_PENDING_MESSAGE);
  const [outcome, setOutcome] = useState(null);
  const [billingPreviewInvoice, setBillingPreviewInvoice] = useState(null);

  const pollIntervalRef = useRef(null);
  const pollAbortRef = useRef(null);
  const outcomeHandledRef = useRef(false);
  const busyRef = useRef(false);
  const settlementInFlightRef = useRef(false);
  const checkoutCanceledRef = useRef(false);

  const stopCheckoutPolling = () => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }

    if (pollAbortRef.current) {
      pollAbortRef.current.abort();
      pollAbortRef.current = null;
    }
  };

  const resetBusy = () => {
    busyRef.current = false;
    setSubmitting(false);
    setPaymentPending(false);
    setPaymentUrl("");
    setProcessingMessage(GYNGER_CHECKOUT_PENDING_MESSAGE);
  };

  const showOutcome = (nextOutcome) => {
    outcomeHandledRef.current = false;
    setOutcome(nextOutcome);
  };

  const handlePaymentSettled = async ({ alreadyPaid = false } = {}) => {
    if (checkoutCanceledRef.current || settlementInFlightRef.current) {
      return;
    }

    settlementInFlightRef.current = true;
    stopCheckoutPolling();
    setPaymentUrl("");
    clearGraceLocksInSession();
    settlementInFlightRef.current = false;

    if (checkoutCanceledRef.current) {
      return;
    }

    resetBusy();
    showOutcome(alreadyPaid ? "already_paid" : "success");
  };

  const handleCheckoutFailed = () => {
    stopCheckoutPolling();
    resetBusy();
    showOutcome("failed");
  };

  const startCheckoutPolling = (sessionId) => {
    stopCheckoutPolling();

    const controller = new AbortController();
    pollAbortRef.current = controller;
    setProcessingMessage(GYNGER_CHECKOUT_PENDING_MESSAGE);
    setPaymentPending(true);

    const pollStatus = async () => {
      const response = await fetchGyngerCheckoutStatus(sessionId, controller.signal);
      if (
        checkoutCanceledRef.current
        || response.canceled
        || controller.signal.aborted
      ) {
        return;
      }

      if (isCheckoutCompleted(response)) {
        await handlePaymentSettled();
        return;
      }

      if (isCheckoutFailed(response)) {
        handleCheckoutFailed();
      }
    };

    pollStatus();
    pollIntervalRef.current = setInterval(pollStatus, CHECKOUT_POLL_INTERVAL_MS);
  };

  const cancelCheckout = () => {
    checkoutCanceledRef.current = true;
    stopCheckoutPolling();
    settlementInFlightRef.current = false;
    setOutcome(null);
    resetBusy();
  };

  const startPayNow = async () => {
    if (!canMakePayment() || busyRef.current) {
      return false;
    }

    // Open synchronously on the click gesture — browsers block window.open after await.
    const paymentWindow = window.open("about:blank", "_blank");
    if (!paymentWindow) {
      toast.error("Pop-up blocked. Allow pop-ups to complete payment.");
      return false;
    }

    checkoutCanceledRef.current = false;
    busyRef.current = true;
    setSubmitting(true);
    setOutcome(null);
    setPaymentUrl("");

    const closePaymentWindow = () => {
      paymentWindow.close();
    };

    const { invoice_id: invoiceId, invoice_amount_due: amountDue } = readGracePeriodFromSession();
    if (!invoiceId) {
      closePaymentWindow();
      await handlePaymentSettled({ alreadyPaid: true });
      return true;
    }

    if (amountDue == null || amountDue === "" || Number.isNaN(Number(amountDue))) {
      closePaymentWindow();
      resetBusy();
      toast.error("Unable to start payment for this invoice.");
      return false;
    }

    const checkoutResponse = await openPaymentSession({
      purpose: "invoice",
      amount: amountDue,
      productDescription: "Usage Metrics Subscription",
      invoiceId,
    });

    if (checkoutResponse.canceled) {
      closePaymentWindow();
      resetBusy();
      return false;
    }

    if (!checkoutResponse.success) {
      closePaymentWindow();
      resetBusy();
      toast.error(checkoutResponse.message || "Unable to open payment session.");
      return false;
    }

    if (isCheckoutCompleted(checkoutResponse)) {
      closePaymentWindow();
      await handlePaymentSettled();
      return true;
    }

    if (checkoutResponse.session_url) {
      setPaymentUrl(checkoutResponse.session_url);
      paymentWindow.location.href = checkoutResponse.session_url;
    } else {
      closePaymentWindow();
    }

    const sessionId = checkoutResponse.session_id || checkoutResponse.id;
    if (!sessionId) {
      closePaymentWindow();
      resetBusy();
      toast.error("Unable to open payment session.");
      return false;
    }

    setSubmitting(false);
    startCheckoutPolling(sessionId);
    return true;
  };

  const startViewBillingDetails = async () => {
    if (!canMakePayment() || busyRef.current) {
      return false;
    }

    busyRef.current = true;
    setSubmitting(true);
    setOutcome(null);

    const { invoice_id: invoiceId, invoice_pdf_path: pdfPath } = readGracePeriodFromSession();
    if (!invoiceId) {
      await handlePaymentSettled({ alreadyPaid: true });
      return true;
    }

    if (!pdfPath) {
      resetBusy();
      toast.info("Invoice PDF is still generating. Please try again shortly.");
      return false;
    }

    setBillingPreviewInvoice({
      invoice_id: invoiceId,
      pdf_path: pdfPath,
    });
    resetBusy();
    return true;
  };

  const billingPreviewFiles = useMemo(() => {
    if (!billingPreviewInvoice?.pdf_path) {
      return [];
    }

    return [{
      key: billingPreviewInvoice.invoice_number || billingPreviewInvoice.pdf_path,
      fileName: getPdfFileName(billingPreviewInvoice.pdf_path),
      getSrc: async (signal) => {
        const previewResponse = await fetchDueInvoicePreviewUrl(
          billingPreviewInvoice.pdf_path,
          signal,
        );
        if (previewResponse.canceled) {
          return null;
        }

        if (!previewResponse.success || !previewResponse.url) {
          return null;
        }

        return previewResponse.url;
      },
    }];
  }, [billingPreviewInvoice]);

  const handleOutcomeClose = () => {
    if (outcomeHandledRef.current) {
      return;
    }

    outcomeHandledRef.current = true;
    setOutcome(null);
  };

  useEffect(() => () => {
    stopCheckoutPolling();
  }, []);

  useEffect(() => {
    if (!outcome || outcome === "failed") {
      return undefined;
    }

    const timerId = setTimeout(() => {
      handleOutcomeClose();
    }, GYNGER_SUCCESS_MODAL_AUTO_CLOSE_MS);

    return () => clearTimeout(timerId);
  }, [outcome]);

  const isPaymentBusy = submitting || paymentPending;
  const outcomeCopy = outcome ? PAYMENT_OUTCOME_COPY[outcome] : null;
  const isFailedOutcome = outcome === "failed";

  return (
    <GraceInvoicePaymentContext.Provider
      value={{
        isPaymentBusy,
        startPayNow,
        startViewBillingDetails,
      }}
    >
      {children}
      <CheckoutProcessingModal
        open={paymentPending}
        message={processingMessage}
        paymentUrl={paymentUrl}
        onCancelPayment={
          processingMessage === GYNGER_CHECKOUT_PENDING_MESSAGE
            ? cancelCheckout
            : undefined
        }
      />
      <SuccessModal
        open={Boolean(outcome)}
        onClose={handleOutcomeClose}
        variant={isFailedOutcome ? "failed" : "success"}
        title={outcomeCopy?.title}
        description={outcomeCopy?.description}
        showCloseButton={isFailedOutcome || outcome === "already_paid"}
      />
      <PdfPreviewModal
        open={Boolean(billingPreviewInvoice)}
        title="Invoice"
        onClose={() => setBillingPreviewInvoice(null)}
        files={billingPreviewFiles}
        errorMessage="Unable to preview this invoice."
        width="40vw"
      />
    </GraceInvoicePaymentContext.Provider>
  );
}

export function useGraceInvoicePayment() {
  return useContext(GraceInvoicePaymentContext);
}
